import React from 'react';

/**
 * Routy, the RouteChef travel buddy. A friendly little helper with a headset
 * (signals "a real person can help") and an orange antenna light tying it to the brand.
 * Pure SVG + CSS animations (LeadBot.css). Moods: idle | wave | think | sad | celebrate.
 * Themes: teal (default) | indigo | sunset.
 */
export const MASCOT_THEMES = {
    teal: { top: '#5EEAD4', bottom: '#0EA5E9', plate: '#0B2536', eye: '#CCFBF1', limb: '#22C3D6', limbEdge: '#0891B2' },
    indigo: { top: '#A5B4FC', bottom: '#6366F1', plate: '#1E1B4B', eye: '#E0E7FF', limb: '#818CF8', limbEdge: '#4F46E5' },
    sunset: { top: '#FDBA74', bottom: '#F43F5E', plate: '#3B0A1A', eye: '#FFE4E6', limb: '#FB7185', limbEdge: '#E11D48' },
};

const ACCENT = '#FF5B15';
const ACCENT_EDGE = '#E04E14';

const Eyes = ({ mood, c }) => {
    if (mood === 'celebrate') {
        return (
            <g fill="none" stroke={c.eye} strokeWidth="2.6" strokeLinecap="round">
                <path d="M28.5 39.5 Q32 35 35.5 39.5" />
                <path d="M44.5 39.5 Q48 35 51.5 39.5" />
            </g>
        );
    }
    // think: eyes glance up and to the right
    const dx = mood === 'think' ? 1.8 : 0;
    const dy = mood === 'think' ? -1.6 : 0;
    return (
        <g className="lb-m-eyes">
            <rect x={28.6 + dx} y={33.5 + dy} width="6.8" height="8.6" rx="3.4" fill={c.eye} />
            <rect x={44.6 + dx} y={33.5 + dy} width="6.8" height="8.6" rx="3.4" fill={c.eye} />
            <circle cx={33.6 + dx} cy={35.6 + dy} r="1.2" fill="#fff" />
            <circle cx={49.6 + dx} cy={35.6 + dy} r="1.2" fill="#fff" />
            {mood === 'sad' && (
                <g fill={c.plate}>
                    {/* droopy lids */}
                    <path d="M27.8 32.5 L36.6 32.5 L36.6 33.6 L27.8 38.2 Z" />
                    <path d="M52.2 32.5 L43.4 32.5 L43.4 33.6 L52.2 38.2 Z" />
                </g>
            )}
        </g>
    );
};

const Mouth = ({ mood, c }) => {
    if (mood === 'think') return <rect x="41" y="45.5" width="5" height="2.4" rx="1.2" fill={c.eye} />;
    if (mood === 'sad') return <path d="M35.5 48.5 Q40 45 44.5 48.5" fill="none" stroke={c.eye} strokeWidth="2.2" strokeLinecap="round" />;
    if (mood === 'celebrate') return <path d="M34 44.5 Q40 52.5 46 44.5 Z" fill={c.eye} />;
    return <path d="M35 45.5 Q40 50 45 45.5" fill="none" stroke={c.eye} strokeWidth="2.2" strokeLinecap="round" />;
};

const Mascot = ({ mood = 'idle', size = 56, theme = 'teal', className = '', title }) => {
    const c = MASCOT_THEMES[theme] || MASCOT_THEMES.teal;
    const gid = `lbGrad-${theme}`;
    const raised = mood === 'wave' || mood === 'celebrate';

    return (
        <svg
            viewBox="0 0 80 80"
            width={size}
            height={size}
            className={`lb-mascot lb-mood-${mood} ${className}`}
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : 'true'}
        >
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0.35" y2="1">
                    <stop offset="0%" stopColor={c.top} />
                    <stop offset="100%" stopColor={c.bottom} />
                </linearGradient>
            </defs>

            <ellipse className="lb-m-shadow" cx="40" cy="76.5" rx="17" ry="2.6" fill="rgba(15,23,42,0.14)" />

            <g className="lb-m-body">
                {/* Antenna with the brand-orange light */}
                <line x1="40" y1="15" x2="40" y2="8.5" stroke={c.limbEdge} strokeWidth="2.2" strokeLinecap="round" />
                <circle className="lb-m-light" cx="40" cy="6.5" r="3.6" fill={ACCENT} />
                <circle cx="38.9" cy="5.4" r="1.1" fill="#FFD3BF" />

                {/* Little body + hands */}
                <rect x="28" y="58" width="24" height="13" rx="6.5" fill={c.limb} stroke={c.limbEdge} strokeWidth="1" />
                <circle cx="20" cy="64" r="4.4" fill={c.limb} stroke={c.limbEdge} strokeWidth="1" />
                {!raised && <circle cx="60" cy="64" r="4.4" fill={c.limb} stroke={c.limbEdge} strokeWidth="1" />}

                {/* Head */}
                <rect x="14" y="14" width="52" height="46" rx="20" fill={`url(#${gid})`} />
                <path d="M24 19 Q33 15.5 42 16.5" fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="2.4" strokeLinecap="round" />

                {/* Face screen */}
                <rect x="20.5" y="26" width="39" height="27" rx="12.5" fill={c.plate} />
                <Eyes mood={mood} c={c} />
                <Mouth mood={mood} c={c} />
                <ellipse cx="25.2" cy="45.5" rx="2.4" ry="1.5" fill="#FB7185" opacity="0.8" />
                <ellipse cx="54.8" cy="45.5" rx="2.4" ry="1.5" fill="#FB7185" opacity="0.8" />

                {/* Headset: ear cups + mic */}
                <rect x="9" y="30" width="7.5" height="15" rx="3.75" fill={ACCENT} stroke={ACCENT_EDGE} strokeWidth="1" />
                <rect x="63.5" y="30" width="7.5" height="15" rx="3.75" fill={ACCENT} stroke={ACCENT_EDGE} strokeWidth="1" />
                <path d="M12.5 44 Q13.5 54 23 55.5" fill="none" stroke={ACCENT_EDGE} strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="24.2" cy="55.6" r="2.2" fill={ACCENT} stroke={ACCENT_EDGE} strokeWidth="0.8" />

                {/* Raised waving hand, drawn on top so it sits in front of the headset */}
                {raised && (
                    <g className="lb-m-wave">
                        <path d="M60 62 Q68 60 72.5 52" fill="none" stroke={c.limbEdge} strokeWidth="2" strokeLinecap="round" />
                        <circle cx="73" cy="50" r="4.4" fill={c.limb} stroke={c.limbEdge} strokeWidth="1" />
                    </g>
                )}
            </g>

            {mood === 'think' && (
                <g className="lb-m-dots" fill="#94A3B8">
                    <circle cx="66" cy="18" r="1.8" />
                    <circle cx="71" cy="12.5" r="2.2" />
                    <circle cx="76" cy="6.5" r="2.5" />
                </g>
            )}

            {mood === 'celebrate' && (
                <g fill="#FBBF24">
                    <path className="lb-m-spark" d="M9 18 l1.6 3.4 3.4 1.6 -3.4 1.6 -1.6 3.4 -1.6 -3.4 -3.4 -1.6 3.4 -1.6z" />
                    <path className="lb-m-spark lb-d2" d="M7 46 l1.2 2.6 2.6 1.2 -2.6 1.2 -1.2 2.6 -1.2 -2.6 -2.6 -1.2 2.6 -1.2z" />
                    <path className="lb-m-spark lb-d3" d="M66 4 l1 2.2 2.2 1 -2.2 1 -1 2.2 -1 -2.2 -2.2 -1 2.2 -1z" />
                </g>
            )}
        </svg>
    );
};

export default Mascot;
